import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { Divider } from 'primereact/divider';
import { useNavigate } from 'react-router-dom';
import styles from './AddNewBillingAgreementDialog.styles.scss';
import cn from 'classnames';
import { AGREEMENT_TYPE } from '../../config/constants';

const AddNewBillingAgreementDialog = ({
  isOpen,
  setIsOpen,
  onSubmit,
  agreementType,
}) => {
  const navigate = useNavigate();

  const STEPS = {
    TERMS_AND_CONDITIONS: 1,
    SUBMISSION: 2,
    SUBMISSION_RESULT: 3,
  };

  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(STEPS.SUBMISSION);
  const [isLoading, setIsLoading] = useState(false);
  const [ref, setRef] = useState('');

  return (
    <Dialog
      className={cn(
        'rounded-4 overflow-hidden',
        styles.addBillingAgreementDialog,
      )}
      visible={isOpen}
      onHide={() => {
        setIsOpen(false);
      }}
      header={
        step === STEPS.TERMS_AND_CONDITIONS ? (
          <h3 className={styles.title}>
            {t('special_billing.terms-and-conditions-0')}
          </h3>
        ) : null
      }
      footer={
        <>
          <Divider className="mb-4" />
          {step === STEPS.TERMS_AND_CONDITIONS && (
            <Button
              onClick={() => setStep(STEPS.SUBMISSION)}
              type="button"
              className="px-5 w-100"
              size="medium"
              label={t('common.ok')}
            />
          )}
          {step === STEPS.SUBMISSION && (
            <div className="d-flex justify-content-between gap-4">
              <Button
                onClick={() => {
                  setIsOpen(false);
                }}
                outlined
                type="button"
                className="px-5 w-100"
                size="medium"
                label={t('common.cancel')}
              />
              <Button
                onClick={() => {
                  setIsLoading(true);
                  onSubmit()
                    .then(({ data }) => {
                      setRef(data.referenceNumber);
                      setStep(STEPS.SUBMISSION_RESULT);
                    })
                    .finally(() => {
                      setIsLoading(false);
                    });
                }}
                loading={isLoading}
                type="button"
                className="px-5 w-100"
                size="medium"
                label={t('common.confirm')}
              />
            </div>
          )}
          {step === STEPS.SUBMISSION_RESULT && (
            <Button
              onClick={() => {
                setIsOpen(false);
                navigate(
                  `/${i18n.language}/additional-functionalities/special-billing`,
                );
              }}
              type="button"
              className="px-5 w-100"
              size="medium"
              label={t('common.home')}
            />
          )}
        </>
      }
    >
      {step === STEPS.TERMS_AND_CONDITIONS && (
        <div className={cn('mt-4', styles.termsAndConditions)}>
          {agreementType === AGREEMENT_TYPE.SELF ? (
            <div
              dangerouslySetInnerHTML={{
                __html: t(
                  'special_billing.self-billing-agreement-terms-and-conditions',
                ),
              }}
            />
          ) : (
            <p
              dangerouslySetInnerHTML={{
                __html: t(
                  'special_billing.third-party-billing-agreement-terms-and-conditions',
                ),
              }}
            />
          )}
        </div>
      )}
      {step === STEPS.SUBMISSION && (
        <div className={styles.submissionMessage}>
          <span>
            <div>
              <i className="pi pi-file-check" />
            </div>
          </span>
          <h3>
            {agreementType === AGREEMENT_TYPE.SELF
              ? t(
                  'special_billing.do-you-confirm-the-submission-of-the-self-billing-agreement',
                )
              : t(
                  'special_billing.do-you-confirm-the-submission-of-the-third-party-billing-agreement',
                )}
          </h3>
          <p>
            {t(
              'special_billing.by-confirming-the-submission-you-acknowledge-that-you-have-carefully-reviewed-all-details-including-the-uploaded-agreement-and-that-the-information-provided-is-correct',
            )}
          </p>
        </div>
      )}
      {step === STEPS.SUBMISSION_RESULT && (
        <div className={styles.submissionResultMessage}>
          <span>
            <div>
              <i className="pi pi-file" />
            </div>
          </span>
          <h3>
            {agreementType === AGREEMENT_TYPE.SELF
              ? t(
                  'special_billing.self-billing-agreement-request-has-been-successfully-submitted',
                )
              : t(
                  'special_billing.third-party-billing-agreement-request-has-been-successfully-submitted',
                )}
          </h3>
          <h5>
            {t('special_billing.request_number_info', {
              number: ref,
            })}
          </h5>
          <p>{t('special_billing.add-self-billing-dialog-desc')}</p>
        </div>
      )}
    </Dialog>
  );
};

export default React.memo(AddNewBillingAgreementDialog);
